import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  SYSTEM_UPDATE_SEEN_KEY_PREFIX,
  SYSTEM_UPDATES,
  SYSTEM_UPDATES_TITLE,
  SystemUpdateEntry,
} from '../config/system-updates.config';

@Injectable()
export class SystemUpdateService {
  private modalShownByUser = new Map<string, boolean>();
  private hasUnseenSrc = new BehaviorSubject<boolean>(false);
  hasUnseenUpdates$ = this.hasUnseenSrc.asObservable();

  get title(): string {
    return SYSTEM_UPDATES_TITLE;
  }

  get history(): SystemUpdateEntry[] {
    const entries = SYSTEM_UPDATES.map((entry) => ({
      ...entry,
      items: entry.items.filter((item) => item.trim().length > 0),
    })).filter((entry) => entry.items.length > 0);
    return entries.length > 0 ? [entries[0]] : [];
  }

  get latest(): SystemUpdateEntry | undefined {
    return SYSTEM_UPDATES[0];
  }

  get buildId(): string {
    const latest = this.latest;
    return latest ? `${latest.version}-${latest.revision}` : '';
  }

  hasUpdates(): boolean {
    return this.hasText(this.latest?.items);
  }

  initForUser(userId: string | number): void {
    this.modalShownByUser.delete(String(userId));
    if (!this.hasUpdates()) {
      this.hasUnseenSrc.next(false);
      return;
    }
    this.hasUnseenSrc.next(this.hasUnseenUpdates(userId));
  }

  hasUnseenUpdates(userId?: string | number): boolean {
    if (!this.hasUpdates() || !this.buildId) {
      return false;
    }
    const id = userId ?? this.getCurrentUserId();
    if (id == null) {
      return false;
    }
    return localStorage.getItem(this.seenKey(id)) !== this.buildId;
  }

  markAsSeen(userId?: string | number): void {
    const id = userId ?? this.getCurrentUserId();
    if (id == null || !this.buildId) {
      return;
    }
    localStorage.setItem(this.seenKey(id), this.buildId);
    this.hasUnseenSrc.next(false);
  }

  consumeAutoModal(userId?: string | number): boolean {
    const id = userId ?? this.getCurrentUserId();
    if (id == null) {
      return false;
    }
    const key = String(id);
    if (this.modalShownByUser.get(key) || !this.hasUnseenUpdates(id)) {
      return false;
    }
    this.modalShownByUser.set(key, true);
    return true;
  }

  onUpdatesPopoverVisible(visible: boolean): void {
    if (visible) {
      this.markAsSeen();
    }
  }

  private hasText(items?: string[]): boolean {
    return (items ?? []).some((item) => item.trim().length > 0);
  }

  private seenKey(userId: string | number): string {
    return `${SYSTEM_UPDATE_SEEN_KEY_PREFIX}${userId}`;
  }

  private getCurrentUserId(): string | number | null {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) {
        return null;
      }
      return JSON.parse(raw)?.adminId ?? null;
    } catch {
      return null;
    }
  }
}
